import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Download, Globe, Clock, ShieldAlert, Mail, RefreshCw, Table,
  UploadCloud, Users, CheckCircle2, Zap
} from 'lucide-react';
import loginImg from '../assets/case-domain/login.jpg';
import dashboardImg from '../assets/case-domain/dashboard.jpg';
import addDomainImg from '../assets/case-domain/add-domain.jpg';
import emptyImg from '../assets/case-domain/empty.jpg';

const CaseStudyDomain = () => {
  const problem = [
    'Excel spreadsheets — dozens or hundreds of domains tracked in static files, expiration dates updated by hand.',
    'Calendar reminders — individual alerts set by IT admins, often forgotten or buried among other tasks.',
    'No centralised view — domain data scattered across registrars, emails and personal notes.',
    'Human error — missed renewals leading to website downtime, lost brand identity and SEO, and squatters acquiring expired domains.',
    'Time wasted — hours spent logging into multiple registrar accounts just to check dates.',
  ];

  const how = [
    { icon: <UploadCloud size={18} />, title: 'Add Domains', text: 'Users add domains manually or bulk-import via CSV.' },
    { icon: <Globe size={18} />, title: 'Automated WHOIS Lookup', text: 'The system queries registration data (expiration date, registrar, creation date) via API.' },
    { icon: <Table size={18} />, title: 'Dashboard Visualisation', text: 'A sortable, filterable table with colour-coded expiry: red (<7 days), yellow (<30 days), green (30+ days).' },
    { icon: <RefreshCw size={18} />, title: 'Scheduled Refresh', text: 'Automatic periodic checks keep the data up to date.' },
    { icon: <Mail size={18} />, title: 'Email Notifications', text: 'Proactive alerts sent 30, 14 and 7 days before expiration.' },
    { icon: <Users size={18} />, title: 'Admin Controls', text: 'Role-based access with user management for teams.' },
  ];

  const stack = [
    ['Frontend', 'React, Tailwind CSS'],
    ['Backend', 'FastAPI (Python)'],
    ['Database', 'MongoDB Atlas'],
    ['WHOIS Data', 'WhoisXML API'],
    ['Email Alerts', 'SendGrid API'],
    ['Hosting', 'Netlify (frontend), Render (backend)'],
  ];

  const benefits = [
    { title: 'Prevent domain loss', text: 'Automated alerts eliminate the risk of forgetting renewals.' },
    { title: 'Save time', text: 'Hours of manual checking reduced to seconds of dashboard viewing.' },
    { title: 'Centralised management', text: 'One platform for all domains, regardless of registrar.' },
    { title: 'Team collaboration', text: 'Multi-user access with admin controls for enterprise use.' },
    { title: 'Cost avoidance', text: 'Avoid premium prices to recover expired domains from squatters.' },
    { title: 'Brand protection', text: 'Ensure continuous online presence and customer trust.' },
    { title: 'Audit trail', text: 'Historical tracking of domain status and changes.' },
  ];

  const comparison = [
    ['Tracking method', 'Excel spreadsheets', 'Automated web dashboard'],
    ['Data freshness', 'Manually updated (often outdated)', 'Real-time WHOIS API data'],
    ['Alerts', 'Calendar reminders (easily missed)', 'Automated email notifications'],
    ['Time investment', 'Hours per month', 'Minutes per month'],
    ['Risk of expiry', 'High', 'Near zero'],
    ['Team access', 'File sharing, version conflicts', 'Role-based multi-user platform'],
  ];

  const screens = [
    { title: 'Secure Login', caption: 'Email/password sign-in to the monitoring platform.', image: loginImg },
    { title: 'Domain Monitor Dashboard', caption: 'All domains in a sortable table with colour-coded expiry status and summary counters.', image: dashboardImg },
    { title: 'Add / Import Domains', caption: 'Add domains manually with tags and optional manual expiry, or bulk-import via CSV.', image: addDomainImg },
    { title: 'Empty State & Onboarding', caption: 'Clear first-run state guiding users to add or import their first domains.', image: emptyImg },
  ];

  return (
    <div className="min-h-screen bg-bg-page" data-testid="case-study-domain">
      <header className="sticky top-0 z-50 bg-bg-page/90 backdrop-blur-md border-b border-border-medium">
        <div className="container mx-auto px-8 py-4 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 nav-link" data-testid="case-back-link">
            <ArrowLeft size={18} /> Back to portfolio
          </Link>
          <a href="/Anna-Krat-CV.pdf" download="Anna-Krat-CV.pdf" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-primary text-white text-sm font-medium hover:bg-brand-hover transition-colors" data-testid="case-download-cv-btn">
            <Download size={16} /> CV
          </a>
        </div>
      </header>

      <main className="container mx-auto px-8 py-16 max-w-5xl">
        <div className="mb-12">
          <span className="px-3 py-1 rounded-full text-xs font-medium bg-brand-soft text-brand-primary border border-indigo-100 mb-5 inline-block">
            Full-stack • Live
          </span>
          <h1 className="heading-1 text-text-primary mb-5">Domain Expiry Watcher</h1>
          <p className="body-large text-text-secondary max-w-3xl">
            A web-based domain monitoring platform that centralises domain tracking, retrieves real-time WHOIS data and sends proactive email alerts before domains expire — replacing manual Excel tracking and easily-missed calendar reminders.
          </p>
          <div className="grid sm:grid-cols-3 gap-6 mt-10">
            {[
              { icon: <Zap className="text-brand-primary" size={22} />, big: '~90% less overhead', small: 'Manual checking reduced to seconds' },
              { icon: <ShieldAlert className="text-brand-primary" size={22} />, big: 'Near-zero expiry risk', small: 'Alerts at 30, 14 and 7 days' },
              { icon: <Globe className="text-brand-primary" size={22} />, big: 'Real-time WHOIS', small: 'Live registrar & expiry data' },
            ].map((s, i) => (
              <div key={i} className="team-card p-6" data-testid={`case-stat-${i}`}>
                <div className="mb-3">{s.icon}</div>
                <div className="heading-5 text-text-primary mb-1">{s.big}</div>
                <div className="body-small text-text-secondary">{s.small}</div>
              </div>
            ))}
          </div>
        </div>

        <section className="team-card p-8 mb-16">
          <h2 className="heading-4 text-text-primary mb-3">My role</h2>
          <p className="body-medium">
            Delivered end-to-end, from idea to implementation — product design, UX/UI, full-stack build, WHOIS and email
            integrations, testing and deployment (built by me with AI agents).
          </p>
        </section>

        <section className="mb-16">
          <h2 className="heading-3 text-text-primary mb-8">The problem: manual domain tracking</h2>
          <ul className="grid md:grid-cols-2 gap-4">
            {problem.map((p, i) => (
              <li key={i} className="team-card p-5 flex items-start gap-3">
                <div className="w-2 h-2 bg-brand-primary rounded-full flex-shrink-0 mt-2" />
                <span className="body-small text-text-primary">{p}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="mb-16">
          <h2 className="heading-3 text-text-primary mb-3">Inside the platform</h2>
          <p className="body-medium mb-8 max-w-3xl">A look at the live application (demo data shown).</p>
          <div className="grid md:grid-cols-2 gap-8">
            {screens.map((screen, i) => (
              <div key={i} className="team-card overflow-hidden" data-testid={`case-screen-${i}`}>
                <a href={screen.image} target="_blank" rel="noopener noreferrer" className="block aspect-[16/10] bg-bg-light border-b border-border-medium overflow-hidden group">
                  <img src={screen.image} alt={screen.title} className="w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-[1.02]" />
                </a>
                <div className="p-6">
                  <h3 className="heading-6 text-text-primary mb-2">{i + 1}. {screen.title}</h3>
                  <p className="body-small text-text-secondary">{screen.caption}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <h2 className="heading-3 text-text-primary mb-8">How it works</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {how.map((h, i) => (
              <div key={i} className="team-card p-6">
                <div className="flex items-center gap-2 mb-2 text-brand-primary">
                  {h.icon}
                  <h3 className="heading-6 text-text-primary">{h.title}</h3>
                </div>
                <p className="body-small text-text-secondary">{h.text}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <h2 className="heading-3 text-text-primary mb-8">Before vs. after</h2>
          <div className="team-card overflow-hidden">
            <div className="hidden sm:grid grid-cols-3 gap-4 px-6 py-4 bg-bg-light border-b border-border-medium">
              <span className="caption text-text-secondary">Aspect</span>
              <span className="caption text-text-secondary">Before (manual)</span>
              <span className="caption text-brand-primary">After (Domain Expiry Watcher)</span>
            </div>
            {comparison.map((row, i) => (
              <div key={i} className={`grid grid-cols-1 sm:grid-cols-3 gap-y-1 gap-x-4 px-6 py-4 ${i !== comparison.length - 1 ? 'border-b border-border-light' : ''}`}>
                <span className="body-small text-text-primary font-medium">{row[0]}</span>
                <span className="body-small text-text-secondary">{row[1]}</span>
                <span className="body-small text-brand-primary">{row[2]}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <h2 className="heading-3 text-text-primary mb-8">Technology stack</h2>
          <div className="team-card overflow-hidden">
            {stack.map(([k, v], i) => (
              <div key={i} className={`grid grid-cols-1 sm:grid-cols-3 gap-2 px-6 py-4 ${i !== stack.length - 1 ? 'border-b border-border-light' : ''}`}>
                <span className="caption text-brand-primary">{k}</span>
                <span className="body-small text-text-primary sm:col-span-2">{v}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <h2 className="heading-3 text-text-primary mb-8">Business benefits</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((b, i) => (
              <div key={i} className="team-card p-6">
                <div className="flex items-start gap-2">
                  <CheckCircle2 size={16} className="text-brand-primary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="heading-6 text-text-primary mb-1">{b.title}</h3>
                    <p className="body-small text-text-secondary">{b.text}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <div className="team-card p-6 mb-16 flex items-start gap-3">
          <Clock size={18} className="text-brand-primary flex-shrink-0 mt-1" />
          <p className="body-small text-text-secondary italic">
            A full-stack domain monitoring platform that replaces manual Excel tracking with automated WHOIS lookups and email
            alerts — integrating WhoisXML API for real-time data and SendGrid for notifications, reducing domain-management
            overhead by around 90% and eliminating the risk of costly domain expirations.
          </p>
        </div>

        <div className="text-center">
          <Link to="/" className="btn-secondary inline-flex items-center gap-2" data-testid="case-back-bottom">
            <ArrowLeft size={18} /> Back to portfolio
          </Link>
        </div>
      </main>
    </div>
  );
};

export default CaseStudyDomain;
